import type { RepScoreResponse } from '@/core/infrastructure/services/RepScoreService';

/**
 * Mock data scenarios for RepScore drawer preview.
 *
 * 3 Trust Levels + At Risk overlay:
 * - starter: STARTER (0-249 pts)
 * - good: GOOD (250-499 pts)
 * - high: HIGH (500+ pts)
 * - at_risk: Warning overlay (late payment in grace period)
 *
 * Usage:
 *   1. Set MOCK_ENABLED = true
 *   2. Set MOCK_SCENARIO to desired state
 *   3. Refresh the page
 */

// ============================================================================
// Configuration - Change these to preview different states
// ============================================================================

export const MOCK_ENABLED = false;

export type MockScenario = 'starter' | 'good' | 'high' | 'at_risk';

export const MOCK_SCENARIO: MockScenario = 'at_risk';

// ============================================================================
// Mock Data Definitions
// ============================================================================

const MOCK_SCENARIOS: Record<MockScenario, RepScoreResponse> = {
  /**
   * STARTER (0-249 points)
   * Brand new user, zero purchases, no benefits unlocked
   */
  starter: {
    currentScore: 70,
    maxScore: 1000,
    scoreLevel: 'starter',
    pssEligible: false,
    maxPssAmount: '0',
    pssCreditPercentage: '0',
    nextLevelThreshold: 250,
    lastUpdated: new Date().toISOString(),
    scoreBreakdown: {
      profileCompletion: {
        score: 30,
        maxScore: 100,
        completionPercentage: 60,
        missingVerifications: ['ID', 'Phone'],
      },
      paymentHistory: {
        score: 0,
        maxScore: 400,
        onTimePayments: 0,
        latePayments: 0,
        missedPayments: 0,
        totalPayments: 0,
        successRate: 0,
      },
      accountAge: {
        score: 40,
        maxScore: 100,
        daysActive: 14,
        accountCreated: '2025-01-01',
      },
      purchaseBehavior: {
        score: 0,
        maxScore: 200,
        totalOrders: 0,
        avgOrderValue: '0',
        lastPurchase: '',
      },
    },
    scoreHistory: [
      { date: '2025-01-22', score: 70, trigger: 'Profile updated', change: 30 },
      { date: '2025-01-21', score: 40, trigger: 'Email verified', change: 20 },
      { date: '2025-01-20', score: 20, trigger: 'Account created', change: 20 },
    ],
  },

  /**
   * GOOD (250-499 points)
   * First tier unlocked - 90% pickup, 10% credit
   */
  good: {
    currentScore: 320,
    maxScore: 1000,
    scoreLevel: 'good',
    pssEligible: true,
    maxPssAmount: '500000',
    pssCreditPercentage: '10',
    nextLevelThreshold: 500,
    lastUpdated: new Date().toISOString(),
    scoreBreakdown: {
      profileCompletion: {
        score: 80,
        maxScore: 100,
        completionPercentage: 80,
        missingVerifications: [],
      },
      paymentHistory: {
        score: 150,
        maxScore: 400,
        onTimePayments: 3,
        latePayments: 0,
        missedPayments: 0,
        totalPayments: 3,
        successRate: 95,
      },
      accountAge: {
        score: 60,
        maxScore: 100,
        daysActive: 45,
        accountCreated: '2024-12-01',
      },
      purchaseBehavior: {
        score: 30,
        maxScore: 200,
        totalOrders: 2,
        avgOrderValue: '150000',
        lastPurchase: '2025-01-18',
      },
    },
    scoreHistory: [
      { date: '2025-01-20', score: 320, trigger: 'On-time payment', change: 50 },
      { date: '2025-01-15', score: 270, trigger: 'Plan completion', change: 100 },
      { date: '2025-01-10', score: 170, trigger: 'ID verified', change: 20 },
    ],
  },

  /**
   * HIGH (500+ points)
   * Top tier - 80% pickup, 20% credit
   */
  high: {
    currentScore: 620,
    maxScore: 1000,
    scoreLevel: 'high',
    pssEligible: true,
    maxPssAmount: '1000000',
    pssCreditPercentage: '20',
    nextLevelThreshold: 1000,
    lastUpdated: new Date().toISOString(),
    scoreBreakdown: {
      profileCompletion: {
        score: 100,
        maxScore: 100,
        completionPercentage: 100,
        missingVerifications: [],
      },
      paymentHistory: {
        score: 320,
        maxScore: 400,
        onTimePayments: 10,
        latePayments: 0,
        missedPayments: 0,
        totalPayments: 10,
        successRate: 100,
      },
      accountAge: {
        score: 100,
        maxScore: 100,
        daysActive: 120,
        accountCreated: '2024-09-01',
      },
      purchaseBehavior: {
        score: 100,
        maxScore: 200,
        totalOrders: 6,
        avgOrderValue: '250000',
        lastPurchase: '2025-01-22',
      },
    },
    scoreHistory: [
      { date: '2025-01-22', score: 620, trigger: 'On-time payment', change: 50 },
      { date: '2025-01-18', score: 570, trigger: 'Plan completion', change: 100 },
      { date: '2025-01-12', score: 470, trigger: 'On-time payment', change: 50 },
      { date: '2025-01-05', score: 420, trigger: 'Referral bonus', change: 30 },
    ],
  },

  /**
   * AT RISK (Warning overlay)
   * Late payment detected, risk of losing benefits
   * Note: This is NOT a trust level - it's a temporary warning state
   */
  at_risk: {
    currentScore: 380,
    maxScore: 1000,
    scoreLevel: 'good', // Still GOOD tier, but at risk
    pssEligible: true,
    maxPssAmount: '500000',
    pssCreditPercentage: '10',
    nextLevelThreshold: 500,
    lastUpdated: new Date().toISOString(),
    scoreBreakdown: {
      profileCompletion: {
        score: 100,
        maxScore: 100,
        completionPercentage: 100,
        missingVerifications: [],
      },
      paymentHistory: {
        score: 180,
        maxScore: 400,
        onTimePayments: 4,
        latePayments: 1,
        missedPayments: 0,
        totalPayments: 5,
        successRate: 80,
      },
      accountAge: {
        score: 70,
        maxScore: 100,
        daysActive: 60,
        accountCreated: '2024-11-01',
      },
      purchaseBehavior: {
        score: 30,
        maxScore: 200,
        totalOrders: 3,
        avgOrderValue: '180000',
        lastPurchase: '2025-01-15',
      },
    },
    scoreHistory: [
      // Recent late payment within grace period window (today)
      { date: new Date().toISOString(), score: 380, trigger: 'Late payment', change: -60 },
      { date: '2025-01-15', score: 440, trigger: 'On-time payment', change: 50 },
      { date: '2025-01-10', score: 390, trigger: 'Plan completion', change: 100 },
    ],
  },
};

/**
 * Get the mock data for the current scenario.
 * Returns null if mocking is disabled.
 */
export function getMockRepScore(): RepScoreResponse | null {
  if (!MOCK_ENABLED) {
    return null;
  }
  return MOCK_SCENARIOS[MOCK_SCENARIO];
}
